import React, { useState, useEffect } from 'react';
import { CabecalhoInterno } from './shared/lista/PecasDaLista';
import { TONE_BADGE, TONE_BORDER, TONE_SURFACE, TONE_TEXT } from '../utils/statusColors';
import { invoiceGroupsService } from '../services/api';
import { fmtDate } from '../utils/formatters';
import CascaDeModal from './shared/CascaDeModal';
import MaisAcoes from './shared/MaisAcoes';
import { usePedirTexto } from './shared/ConfirmDialog';
import { campo } from '../design';

const fmt = (v: any) => {
  const n = parseFloat(String(v));
  return isNaN(n) ? '0,00' : n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

interface Props {
  groupId: number;
  onClose: () => void;
  onUpdate: () => void;
}

export default function InvoiceGroupDetailModal({ groupId, onClose, onUpdate }: Props) {
  const pedirTexto = usePedirTexto();
  const [group, setGroup] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [emitting, setEmitting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);
  const [showSendModal, setShowSendModal] = useState(false);
  const [selectedEmails, setSelectedEmails] = useState<Record<string, boolean>>({});
  const [customEmail, setCustomEmail] = useState('');
  const [customNote, setCustomNote] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const data = await invoiceGroupsService.getOne(groupId);
      setGroup(data);
    } catch (err: any) {
      setFeedback({ type: 'error', msg: err?.message || 'Erro ao carregar grupo' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [groupId]);

  const handleEmitNfse = async () => {
    if (!window.confirm('Emitir NFS-e única para este grupo? Valor total: R$ ' + fmt(group.totalValue))) return;
    setEmitting(true);
    setFeedback(null);
    try {
      const result = await invoiceGroupsService.emitNfse(groupId);
      setFeedback({
        type: 'success',
        msg: `NFS-e #${result.fiscalDocumentId} criada (status ${result.status}). Aguarde processamento.`,
      });
      await load();
      onUpdate();
    } catch (err: any) {
      setFeedback({ type: 'error', msg: err?.response?.data?.message || err?.message || 'Erro ao emitir NFS-e' });
    } finally {
      setEmitting(false);
    }
  };

  const handleViewPdf = async () => {
    try {
      const blob = await invoiceGroupsService.downloadPdf(groupId);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err: any) {
      setFeedback({ type: 'error', msg: err?.response?.data?.message || err?.message || 'Erro ao gerar PDF' });
    }
  };

  const openSendModal = () => {
    const financialEmails: string[] = [];
    if (group?.client?.emails) {
      for (const e of group.client.emails) {
        if (e.type === 'Financeiro' && e.email) financialEmails.push(e.email);
      }
    }
    if (financialEmails.length === 0 && group?.client?.emailFinancial) {
      financialEmails.push(group.client.emailFinancial);
    }
    const initial: Record<string, boolean> = {};
    for (const e of financialEmails) initial[e] = true;
    setSelectedEmails(initial);
    setCustomEmail('');
    setCustomNote('');
    setShowSendModal(true);
  };

  const handleSendEmail = async () => {
    const checked = Object.keys(selectedEmails).filter(e => selectedEmails[e]);
    const custom = customEmail.trim();
    const allEmails = Array.from(new Set([
      ...checked,
      ...(custom ? custom.split(',').map(s => s.trim()).filter(Boolean) : []),
    ]));
    if (allEmails.length === 0) {
      setFeedback({ type: 'error', msg: 'Selecione ao menos 1 destinatário' });
      return;
    }
    setSendingEmail(true);
    setFeedback(null);
    try {
      const result = await invoiceGroupsService.sendEmail(groupId, {
        emails: allEmails,
        customNote: customNote.trim() || undefined,
      });
      if (result.success) {
        setFeedback({
          type: 'success',
          msg: `Email enviado para: ${(result.sentTo || []).join(', ')}${result.failed ? ' | Falhou: ' + result.failed.join(', ') : ''}`,
        });
        setShowSendModal(false);
        await load();
        onUpdate();
      } else {
        setFeedback({ type: 'error', msg: result.error || 'Falha ao enviar email' });
      }
    } catch (err: any) {
      setFeedback({ type: 'error', msg: err?.response?.data?.message || err?.message || 'Erro' });
    } finally {
      setSendingEmail(false);
    }
  };

  const handleCancel = async () => {
    // Era window.prompt: caixa nativa branca, sem dizer que a ação é irreversível.
    const reason = await pedirTexto({
      title: 'Cancelar grupo de parcelas?',
      consequence: 'Cancela todas as faturas não-pagas do grupo. As pagas ficam como estão.',
      label: 'Motivo do cancelamento',
      obrigatorio: true,
      danger: true,
      confirmLabel: 'Cancelar grupo',
      cancelLabel: 'Voltar',
    });
    if (!reason) return;
    setCancelling(true);
    setFeedback(null);
    try {
      await invoiceGroupsService.cancel(groupId, reason);
      setFeedback({ type: 'success', msg: 'Grupo cancelado.' });
      await load();
      onUpdate();
    } catch (err: any) {
      setFeedback({ type: 'error', msg: err?.response?.data?.message || err?.message || 'Erro ao cancelar' });
    } finally {
      setCancelling(false);
    }
  };

  const statusColor = (s: string) => {
    if (s === 'Quitado') return TONE_BADGE.success;
    if (s === 'Cancelado') return TONE_BADGE.danger;
    if (s === 'Ativo') return TONE_BADGE.progress;
    return TONE_BADGE.neutral;
  };

  const invStatusColor = (s: string) => {
    if (s === 'Paga') return TONE_BADGE.success;
    if (s === 'Cancelada') return TONE_BADGE.danger;
    if (s === 'Aberta') return TONE_BADGE.open;
    if (s === 'Parcial') return TONE_BADGE.warning;
    return TONE_BADGE.neutral;
  };

  return (
    <>
    {/* Grupo de PARCELAS: selo `info`, nunca o violeta do grupo econômico (§4).
        Não fecha ao clicar fora (como já era). */}
    <CascaDeModal
      largura="xl"
      titulo={`Grupo de Faturamento ${group?.code || `#${groupId}`}`}
      subtitulo={group ? (group.client?.name || group.client?.companyName || undefined) : undefined}
      selos={group ? (
        <>
          <span className={`px-2 py-0.5 rounded text-xs font-medium ${TONE_BADGE.info}`}>
            {group.installments}x parcelas
          </span>
          <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusColor(group.status)}`}>
            {group.status}
          </span>
          {group.nfseStatus && (
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${invStatusColor(group.nfseStatus === 'Autorizada' ? 'Paga' : group.nfseStatus)}`}>
              NFS-e: {group.nfseStatus}
            </span>
          )}
        </>
      ) : undefined}
      onClose={onClose}
      faixa={group && !loading ? [
        { k: 'Valor total', v: `R$ ${fmt(group.totalValue)}` },
        { k: 'Parcelas', v: `${group.installments}x` },
        { k: 'Período', v: `${fmtDate(group.periodStart)} a ${fmtDate(group.periodEnd)}` },
        { k: 'Emissão', v: fmtDate(group.issueDate) },
        { k: 'NFS-e', v: group.nfseDocumentId ? `#${group.nfseDocumentId}` : 'Não emitida' },
      ] : undefined}
      corpoClassName="p-6 space-y-5"
      rodape={
        <>
          {/* Cancelar o grupo é destrutivo e raro: vai para o ⋯ (§8.1). */}
          {group?.status === 'Ativo' && (
            <MaisAcoes
              abrePara="cima"
              itens={[{
                label: cancelling ? 'Cancelando...' : 'Cancelar grupo',
                danger: true,
                disabled: cancelling,
                onClick: handleCancel,
              }]}
            />
          )}
          <div className="ml-auto flex gap-2">
            {group && (
              <button
                onClick={handleViewPdf}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded text-sm font-medium"
                title="Visualiza o PDF da Fatura do Grupo (R$ total + tabela das parcelas)"
              >📄 Ver Fatura PDF</button>
            )}
            {group?.status === 'Ativo' && !group?.nfseDocumentId && (
              <button
                onClick={handleEmitNfse}
                disabled={emitting}
                className="px-4 py-2 bg-primary hover:bg-primary-strong text-white rounded text-sm font-medium disabled:opacity-50"
              >{emitting ? 'Emitindo...' : 'Emitir NFS-e Única'}</button>
            )}
            {group?.nfseStatus === 'Autorizada' && (
              <button
                onClick={openSendModal}
                disabled={sendingEmail}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded text-sm font-medium disabled:opacity-50"
                title="Envia 1 email único com NFS-e + N boletos pros destinatários selecionados"
              >{sendingEmail ? 'Enviando...' : '✉️ Enviar Email'}</button>
            )}
            <button
              onClick={onClose}
              className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded text-sm"
            >Fechar</button>
          </div>
        </>
      }
    >
          {loading && <div className="text-slate-400 text-center py-8">Carregando...</div>}

          {feedback && (
            <div className={`p-3 rounded text-sm border ${
              feedback.type === 'success'
                ? `${TONE_SURFACE.success} ${TONE_BORDER.success} ${TONE_TEXT.success}`
                : `${TONE_SURFACE.danger} ${TONE_BORDER.danger} ${TONE_TEXT.danger}`
            }`}>{feedback.msg}</div>
          )}

          {group && !loading && (
            <>
              <div>
                <div className="text-sm text-slate-400 mb-1">Descrição (NFS-e)</div>
                <div className="text-white p-3 bg-central-fundo/50 border border-central-borda rounded text-sm">
                  {group.description}
                </div>
              </div>

              <div>
                <div className="text-sm text-slate-400 mb-2">Itens (R$ {fmt(group.totalValue)})</div>
                <table className="w-full text-sm">
                  <CabecalhoInterno>
                    <tr className="text-xs text-slate-400 border-b border-central-borda">
                      <th className="text-left py-2">Item</th>
                      <th className="text-right py-2">Qtd</th>
                      <th className="text-right py-2">Vlr Unit</th>
                      <th className="text-right py-2">Total</th>
                    </tr>
                  </CabecalhoInterno>
                  <tbody>
                    {(group.items || []).map((it: any, i: number) => (
                      <tr key={i} className="border-b border-central-borda">
                        <td className="py-2 text-white">{it.name}</td>
                        <td className="py-2 text-right text-slate-300">{it.quantity}</td>
                        <td className="py-2 text-right text-slate-300">R$ {fmt(it.unitValue)}</td>
                        <td className="py-2 text-right text-white">R$ {fmt(it.totalValue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div>
                <div className="text-sm text-slate-400 mb-2">
                  Faturas geradas ({group.invoices?.length || 0}/{group.installments})
                </div>
                <table className="w-full text-sm">
                  <CabecalhoInterno>
                    <tr className="text-xs text-slate-400 border-b border-central-borda">
                      <th className="text-left py-2">Parcela</th>
                      <th className="text-left py-2">Código</th>
                      <th className="text-left py-2">Vencimento</th>
                      <th className="text-right py-2">Valor</th>
                      <th className="text-left py-2">Status</th>
                      <th className="text-left py-2">Boleto</th>
                    </tr>
                  </CabecalhoInterno>
                  <tbody>
                    {(group.invoices || [])
                      .slice()
                      .sort((a: any, b: any) => (a.installmentNumber || 0) - (b.installmentNumber || 0))
                      .map((inv: any) => (
                        <tr key={inv.id} className="border-b border-central-borda">
                          <td className="py-2 text-white">{inv.installmentNumber}/{inv.totalInstallments}</td>
                          <td className="py-2 text-slate-300">{inv.code}</td>
                          <td className="py-2 text-slate-300">{fmtDate(inv.dueDate)}</td>
                          <td className="py-2 text-right text-white">R$ {fmt(inv.totalValue)}</td>
                          <td className="py-2">
                            <span className={`px-2 py-0.5 rounded text-xs ${invStatusColor(inv.status)}`}>
                              {inv.status}
                            </span>
                          </td>
                          <td className="py-2 text-slate-400 text-xs">
                            {inv.boletoStatus || '—'}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>

              {group.notes && (
                <div className="p-3 bg-central-fundo/50 border border-central-borda rounded text-sm text-slate-300">
                  <div className="text-xs text-slate-500 mb-1">Notas</div>
                  {group.notes}
                </div>
              )}
            </>
          )}
    </CascaDeModal>

      {/* Destinatários e nota digitados: não fecha ao clicar fora (como já era). */}
      {showSendModal && group && (
        <CascaDeModal
          largura="xs"
          zIndex="z-[60]"
          titulo="Enviar Email do Grupo"
          subtitulo={`${group.code} · ${group.invoices?.length || 0} parcelas · R$ ${fmt(group.totalValue)}`}
          onClose={() => setShowSendModal(false)}
          corpoClassName="p-5 space-y-4"
          rodape={
            <div className="ml-auto flex gap-2">
              <button
                onClick={() => setShowSendModal(false)}
                disabled={sendingEmail}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded text-sm disabled:opacity-50"
              >Cancelar</button>
              <button
                onClick={handleSendEmail}
                disabled={sendingEmail}
                className="px-4 py-2 bg-primary hover:bg-primary-strong text-white rounded text-sm font-medium disabled:opacity-50"
              >{sendingEmail ? 'Enviando...' : 'Enviar'}</button>
            </div>
          }
        >
              <div>
                <label className="block text-xs text-slate-400 uppercase mb-2">Destinatários</label>
                {Object.keys(selectedEmails).length === 0 ? (
                  <div className="text-sm text-amber-400 p-2 bg-amber-500/10 rounded border border-amber-500/30">
                    Cliente sem email financeiro cadastrado. Use "Email adicional" abaixo.
                  </div>
                ) : (
                  <div className="space-y-1">
                    {Object.keys(selectedEmails).map(email => (
                      <label key={email} className="flex items-center gap-2 px-3 py-2 bg-slate-700/50 rounded hover:bg-slate-700 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedEmails[email]}
                          onChange={(e) => setSelectedEmails(prev => ({ ...prev, [email]: e.target.checked }))}
                          className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-orange-500 focus:ring-orange-500"
                        />
                        <span className="text-sm text-white">{email}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-xs text-slate-400 uppercase mb-1">Email adicional (teste)</label>
                <input
                  type="email"
                  value={customEmail}
                  onChange={(e) => setCustomEmail(e.target.value)}
                  placeholder="email@exemplo.com"
                  className={`w-full px-3 py-2 ${campo} rounded text-sm`}
                />
              </div>

              <div>
                <label className="block text-xs text-slate-400 uppercase mb-1">Nota no email (opcional)</label>
                <textarea
                  value={customNote}
                  onChange={(e) => setCustomNote(e.target.value)}
                  rows={2}
                  placeholder="Ex: Favor desconsiderar o boleto enviado anteriormente."
                  className={`w-full px-3 py-2 ${campo} rounded text-sm`}
                />
              </div>
        </CascaDeModal>
      )}
    </>
  );
}
